import { useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useBodyScrollLock, useEscapeKey, useFocusTrap, usePortalTarget } from '@/lib/dom';
import { cn } from './cn';

/**
 * A modal dialog with the three behaviours that make one usable and are always the ones
 * omitted: Escape closes it, focus is trapped inside while it is open, and focus returns
 * to whatever opened it on close. All three come from `@/lib/dom`, which the navigation
 * drawer shares — two implementations of a focus trap is one too many.
 *
 * Rendered through a portal, and only after mount: `document` does not exist during SSR,
 * and a dialog has nothing to contribute to a server-rendered document anyway.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const portal = usePortalTarget();

  useEscapeKey(open, onClose);
  useBodyScrollLock(open);
  useFocusTrap(panelRef, open);

  if (!open || !portal) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* The backdrop is a sibling, not a parent: a click target that wraps the panel
          swallows clicks inside it unless every one of them stops propagation. */}
      <div className="absolute inset-0 bg-ink/50" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'relative z-10 flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-card',
          className,
        )}
      >
        <header className="flex items-center justify-between border-b border-line px-5 py-4">
          <h2 className="text-xl font-extrabold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted transition hover:text-red"
            aria-label="Close"
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <footer className="border-t border-line px-5 py-4">{footer}</footer>}
      </div>
    </div>,
    portal,
  );
}
